import React, { useCallback } from 'react';
import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
} from '@dnd-kit/core';
import { sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import { toast } from 'react-hot-toast';
import { useLanguage } from '@/state/language/LanguageProvider';
import type { AeTemplateDetail } from '@/types/assessmentEngine';
import { handleTemplateBuilderDragEnd } from './templateBuilderDragHandlers';
import { parseDndId } from './builderDnd';

interface TemplateBuilderDndProps {
  template: AeTemplateDetail;
  templateId: string;
  readOnly?: boolean;
  onReload: () => void;
  children: React.ReactNode;
}

/** Shared drag context for reordering/moving sections, subsections, and questions. */
export const TemplateBuilderDnd: React.FC<TemplateBuilderDndProps> = ({
  template,
  templateId,
  readOnly,
  onReload,
  children,
}) => {
  const { t } = useLanguage();
  const [activeLabel, setActiveLabel] = React.useState<string | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const onDragStart = useCallback((event: DragStartEvent) => {
    const parsed = parseDndId(String(event.active.id));
    if (!parsed) {
      setActiveLabel(null);
      return;
    }
    setActiveLabel(
      parsed.kind === 'question'
        ? t('assessmentEngine.builder.dragQuestion')
        : t('assessmentEngine.builder.dragSection')
    );
  }, [t]);

  const onDragEnd = useCallback(
    async (event: DragEndEvent) => {
      setActiveLabel(null);
      if (readOnly) return;
      try {
        await handleTemplateBuilderDragEnd(event, template, templateId, onReload);
      } catch (error) {
        console.error('Template builder drag failed', error);
        toast.error(t('assessmentEngine.builder.moveFailed'));
      }
    },
    [readOnly, template, templateId, onReload, t]
  );

  const onDragCancel = useCallback(() => setActiveLabel(null), []);

  if (readOnly) {
    return <>{children}</>;
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={onDragStart}
      onDragEnd={(e) => void onDragEnd(e)}
      onDragCancel={onDragCancel}
    >
      {children}
      <DragOverlay>
        {activeLabel ? (
          <div className="rounded-md border border-[var(--color-accent-bg)] bg-[var(--color-surface-bg)] px-3 py-2 text-sm shadow-lg">
            {activeLabel}
          </div>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
};
